import { Package, ArrowLeft } from 'lucide-react';
import { Img } from './Img';
import { UserAvatar } from './UserAvatar';
import { colors } from '../constants/colors';
import type { Item, AuctionState, Bid, BinSale } from '../types';

interface AuctionOverlayProps {
  displayItem: Item | null;
  activeAuction: AuctionState | null;
  activeBinSale: BinSale | null;
  auctionEnded: { winnerId: string | null; winnerUsername: string | null; winningBid: number | null } | null;
  recentBids: Bid[];
  startingPrice: number;
  highBidderAvatarUrl: string | null;
  winnerAvatarUrl: string | null;
  onEndBinSale: () => void;
  onOpenInventory: () => void;
  endBinLoading: boolean;
}

type AuctionStatus = 'waiting' | 'on_deck' | 'live' | 'sold' | 'no_winner' | 'bin_live';

export function AuctionOverlay({
  displayItem,
  activeAuction,
  activeBinSale,
  auctionEnded,
  recentBids,
  startingPrice,
  highBidderAvatarUrl,
  winnerAvatarUrl,
  onEndBinSale,
  onOpenInventory,
  endBinLoading,
}: AuctionOverlayProps) {
  const getStatus = (): AuctionStatus => {
    if (activeBinSale && activeBinSale.status === 'LIVE') return 'bin_live';
    if (activeAuction) return 'live';
    if (auctionEnded) {
      return auctionEnded.winnerId ? 'sold' : 'no_winner';
    }
    if (displayItem) return 'on_deck';
    return 'waiting';
  };

  const status = getStatus();

  const getStatusDisplay = () => {
    switch (status) {
      case 'waiting':
        return { text: 'Select an item from inventory', color: 'rgba(255,255,255,0.7)', bgColor: 'rgba(0,0,0,0.3)' };
      case 'on_deck':
        return { text: 'Ready to start', color: '#fff', bgColor: 'rgba(59,130,246,0.85)' };
      case 'live':
        return { text: 'Auction Live', color: '#fff', bgColor: 'rgba(236,72,153,0.9)' };
      case 'bin_live':
        return { text: `Buy Now \u2014 ${activeBinSale?.remainingQuantity || 0} left`, color: '#fff', bgColor: 'rgba(245,158,11,0.9)' };
      case 'sold':
        return { text: `Sold for $${Math.ceil(auctionEnded?.winningBid || 0)}!`, color: '#fff', bgColor: 'rgba(34,197,94,0.9)' };
      case 'no_winner':
        return { text: 'No winner', color: '#fff', bgColor: 'rgba(107,114,128,0.85)' };
    }
  };

  const statusDisplay = getStatusDisplay();
  const canEndBin = status === 'bin_live';

  return (
    <div style={styles.container}>
      {/* Item Card */}
      {displayItem ? (
        <div style={styles.itemCard}>
          <div style={styles.itemCardContent}>
            {displayItem.imageUrl ? (
              <Img
                src={displayItem.imageUrl}
                alt={displayItem.name}
                variant="thumb"
                renderedWidth={80}
                blurhash={displayItem.blurhash}
                style={styles.itemCardImage}
              />
            ) : (
              <div style={styles.itemCardImagePlaceholder}>
                <Package size={20} color="rgba(255,255,255,0.5)" />
              </div>
            )}
            <div style={styles.itemCardInfo}>
              <span style={styles.itemCardName}>{displayItem.name}</span>
              {displayItem.description && (
                <span style={styles.itemCardDescription}>{displayItem.description}</span>
              )}
            </div>
            <div style={styles.itemCardRight}>
              {status === 'live' && activeAuction ? (
                <>
                  <span style={styles.itemCardPrice}>${Math.ceil(activeAuction.currentPrice)}</span>
                  <span style={{
                    ...styles.itemCardTimer,
                    color: activeAuction.millisecondsRemaining <= 10000 ? '#FF2D55' : 'rgba(255,255,255,0.7)',
                  }}>
                    {(activeAuction.millisecondsRemaining / 1000).toFixed(1)}s
                  </span>
                  <span style={styles.itemCardMeta}>{activeAuction.bidCount} bids</span>
                  {activeAuction.highestBidderId && (
                    <span style={{ ...styles.itemCardHighBidder, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <UserAvatar src={highBidderAvatarUrl} size={14} alt={activeAuction.highestBidderUsername || 'Bidder'} />
                      <span>{activeAuction.highestBidderUsername || 'Bidder'} winning</span>
                    </span>
                  )}
                </>
              ) : status === 'sold' || status === 'no_winner' ? (
                <>
                  <span style={styles.itemCardPrice}>
                    {auctionEnded?.winnerId ? `$${Math.ceil(auctionEnded.winningBid || 0)}` : 'No sale'}
                  </span>
                  <span style={{ ...styles.itemCardHighBidder, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    {auctionEnded?.winnerId ? (
                      <>
                        <UserAvatar src={winnerAvatarUrl} size={14} alt={auctionEnded.winnerUsername || 'Winner'} />
                        <span style={{ color: '#4ade80' }}>{auctionEnded.winnerUsername || 'Winner'} won</span>
                      </>
                    ) : (
                      <span>No bids</span>
                    )}
                  </span>
                </>
              ) : status === 'bin_live' && activeBinSale ? (
                <>
                  <span style={styles.itemCardPrice}>${activeBinSale.finalPrice.toFixed(2)}</span>
                  <span style={{ ...styles.itemCardMeta, color: '#4ade80' }}>BIN Live</span>
                  <span style={styles.itemCardMeta}>{activeBinSale.remainingQuantity} left</span>
                </>
              ) : (
                <span style={styles.itemCardPrice}>${startingPrice}</span>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Status Bar — skipped on on_deck and waiting since those have
          their own UI (Start Auction button / Select Item button). */}
      {status !== 'on_deck' && status !== 'waiting' && (
        <div style={{
          ...styles.statusBar,
          backgroundColor: statusDisplay.bgColor,
          color: statusDisplay.color,
        }}>
          {statusDisplay.text}
        </div>
      )}

      {/* Recent Bids */}
      {status === 'live' && recentBids.length > 0 && (
        <div style={styles.bidsContainer}>
          {recentBids.slice(0, 3).map((bid, i) => (
            <div key={i} style={styles.bidItem}>
              <span>{bid.bidderName || bid.bidderId.slice(0, 8)}</span>
              <span style={styles.bidAmount}>${bid.amount}</span>
            </div>
          ))}
        </div>
      )}

      {/* End BIN Sale */}
      {canEndBin && (
        <div style={styles.actionRow}>
          <button
            onClick={onEndBinSale}
            disabled={endBinLoading}
            style={styles.endBinButton}
          >
            {endBinLoading ? 'Ending...' : 'End Sale'}
          </button>
        </div>
      )}

      {/* No item — prompt */}
      {status === 'waiting' && (
        <button onClick={onOpenInventory} style={styles.selectItemButton}>
          <ArrowLeft size={16} />
          Select Item from Inventory
        </button>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  itemCard: {
    backgroundColor: 'rgba(255,255,255,0.12)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: '12px',
    padding: '10px',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  itemCardContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  itemCardImage: {
    width: '52px',
    height: '52px',
    objectFit: 'cover',
    borderRadius: '8px',
    flexShrink: 0,
  },
  itemCardImagePlaceholder: {
    width: '52px',
    height: '52px',
    borderRadius: '8px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemCardInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  itemCardName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#fff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemCardDescription: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.6)',
    marginTop: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemCardRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  itemCardPrice: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#fff',
  },
  itemCardTimer: {
    fontSize: '13px',
    fontWeight: 600,
    marginTop: '1px',
  },
  itemCardMeta: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.6)',
  },
  itemCardHighBidder: {
    fontSize: '11px',
    fontWeight: 600,
    marginTop: '2px',
    color: 'rgba(255,255,255,0.8)',
  },
  statusBar: {
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 600,
    textAlign: 'center',
  },
  bidsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '6px 10px',
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: '8px',
  },
  bidItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: 'rgba(255,255,255,0.7)',
    padding: '2px 0',
  },
  bidAmount: {
    fontWeight: 600,
    color: '#fff',
  },
  actionRow: {
    display: 'flex',
    gap: '6px',
  },
  endBinButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'rgba(220,38,38,0.85)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  selectItemButton: {
    padding: '12px',
    backgroundColor: colors.accent.purple,
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
};
